import { useCallback, useEffect, useRef, useState } from "react";
import {
  useLocation,
  useParams,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import {
  formatMoney,
  reconcileSavedPaymentMethodSelection,
} from "@bw/shared/utils";
import {
  useFrontendConfig,
  useWalletFrontendConfig,
  useAuth,
  usePaymentMethods,
  useWalletSetupIntent,
  paymentQueryKeys,
} from "@bw/shared/hooks";
import type { SubscriptionResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { PaymentMethodsLoadError } from "../../../common/components/PaymentMethodsLoadError";
import {
  MaterialAddCardForm,
  MaterialConnectedElementsProvider,
  MaterialPaymentMethodPicker,
  MaterialPaymentIntentAuthentication,
} from "../../../common/components/payment";
import { useFacilityPlans } from "../hooks/useFacilityPlans";
import { useSubscribe } from "../hooks/useSubscribe";
import { useConfirmSubscription } from "../hooks/useConfirmSubscription";
import { planTypeDisplay, accessWindowLabel } from "../lib/planDisplay";
import { deriveSubscriptionView } from "../lib/subscriptionState";
import { PurchaseConfirmation } from "../components/PurchaseConfirmation";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";

/**
 * Plan detail + PURCHASE (`/facility/:facilityId/memberships/:planId`). Reuses
 * the shared platform wallet + PaymentMethodPicker and AddCardForm, then a single
 * POST /memberships/subscribe. Authentication uses the returned immutable
 * connected-account route, not current facility routing. The recurring charge
 * happens SERVER-SIDE; success requires an active, settled subscription.
 *
 * Zero client-side money math: the price + visit allotment are the plan's
 * VenuePlanResponse verbatim; only the cadence word is derived from plan_type for
 * the truthful auto-renew disclosure.
 */
export function PlanDetailPage() {
  const { facilityId, planId } = useParams<{
    facilityId: string;
    planId: string;
  }>();
  const { user } = useAuth();
  if (!user || !facilityId || !planId) return null;
  return (
    <PlanDetailController
      key={`${user.uid}:${facilityId}:${planId}`}
      actorId={user.uid}
    />
  );
}

function PlanDetailController({ actorId }: { actorId: string }) {
  const { facilityId, planId } = useParams<{
    facilityId: string;
    planId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const facilitySearchReturn = readFacilityDiscoveryReturn(location.search);

  const plansQ = useFacilityPlans(facilityId);
  const plan = (plansQ.data ?? []).find((p) => p.plan_id === planId);

  const paymentSelectionContext = `${facilityId ?? "missing"}:${planId ?? "missing"}`;
  const [paymentSelection, setPaymentSelection] = useState(() => ({
    context: paymentSelectionContext,
    paymentMethodId: null as string | null,
  }));
  const selectedPmId =
    paymentSelection.context === paymentSelectionContext
      ? paymentSelection.paymentMethodId
      : null;
  const selectPaymentMethod = useCallback(
    (paymentMethodId: string | null) => {
      setPaymentSelection({
        context: paymentSelectionContext,
        paymentMethodId,
      });
    },
    [paymentSelectionContext],
  );
  const initializedPaymentContextRef = useRef<string | null>(null);
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  const [success, setSuccess] = useState<SubscriptionResponse | null>(null);
  const [pendingPurchase, setPendingPurchase] =
    useState<SubscriptionResponse | null>(null);
  const [uncertainPurchase, setUncertainPurchase] = useState(false);
  // SCA (3-D Secure): when the first charge needs authentication the backend
  // returns the incomplete sub + the first-invoice PaymentIntent client_secret.
  // We hold it here and drive stripe.confirmCardPayment before confirming the
  // subscription server-side — success is shown ONLY once it is active.
  const [pendingSca, setPendingSca] = useState<{
    subscriptionId: string;
    clientSecret: string;
    account: string;
  } | null>(null);
  // True when the SCA challenge or the server reconcile came back not-cleared —
  // the member gets an honest retry, never a fabricated success.
  const [scaError, setScaError] = useState(false);

  // The payment spine — this screen IS the pay step, so the reads fire eagerly.
  const configQ = useFrontendConfig(facilityId);
  const methodsQ = usePaymentMethods(facilityId);
  const setupIntent = useWalletSetupIntent();
  const subscribe = useSubscribe();
  const confirmSubscription = useConfirmSubscription();
  const paymentLocked =
    subscribe.isPending ||
    confirmSubscription.isPending ||
    pendingSca !== null ||
    pendingPurchase !== null ||
    uncertainPurchase ||
    cardSetupBusy;

  const createSetupIntentSecret = useCallback(
    async (idempotencyKey: string) => {
      const res = await setupIntent.mutateAsync(idempotencyKey);
      return res.client_secret;
    },
    [setupIntent],
  );

  const methods = methodsQ.data ?? [];
  const paymentMethodsUnavailable = methodsQ.isError && pendingSca === null;
  const paymentMethodsSettled =
    methodsQ.data !== undefined && !methodsQ.isFetching && !methodsQ.isError;
  useEffect(() => {
    if (!paymentMethodsSettled || methodsQ.data === undefined) return;
    const initializeProviderDefault =
      initializedPaymentContextRef.current !== paymentSelectionContext;
    initializedPaymentContextRef.current = paymentSelectionContext;
    setPaymentSelection((current) => {
      const currentId =
        current.context === paymentSelectionContext
          ? current.paymentMethodId
          : null;
      const paymentMethodId = reconcileSavedPaymentMethodSelection(
        methodsQ.data,
        currentId,
        initializeProviderDefault,
      );
      if (
        current.context === paymentSelectionContext &&
        current.paymentMethodId === paymentMethodId
      ) {
        return current;
      }
      return { context: paymentSelectionContext, paymentMethodId };
    });
  }, [methodsQ.data, paymentMethodsSettled, paymentSelectionContext]);
  const chargeReadyPmId =
    paymentMethodsSettled && methodsQ.data !== undefined
      ? reconcileSavedPaymentMethodSelection(methodsQ.data, selectedPmId, false)
      : null;

  // ---- Success → confirmation (must precede the plan-state guards) ----------
  if (success) {
    return (
      <PurchaseConfirmation
        subscription={success}
        facilityId={facilityId ?? ""}
      />
    );
  }

  // ---- Plan load states ------------------------------------------------------
  if (plansQ.isLoading) {
    return (
      <Page>
        <Stack
          direction="row"
          spacing={1.5}
          role="status"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading plan…</Typography>
        </Stack>
      </Page>
    );
  }

  if (plansQ.isError || !plan) {
    return (
      <Page>
        <Alert
          severity="error"
          action={
            <Button
              component={RouterLink}
              to={withFacilityDiscoveryReturn(
                `/facility/${facilityId}/memberships`,
                facilitySearchReturn,
              )}
              color="inherit"
              size="small"
            >
              Back to plans
            </Button>
          }
        >
          We couldn&apos;t load this plan. It may no longer be offered.
        </Alert>
      </Page>
    );
  }

  const display = planTypeDisplay(plan.plan_type, plan.billing_interval);
  const isPass = display.framing === "pass";
  const access = accessWindowLabel(plan);
  const priceLabel = `${formatMoney(plan.price, plan.currency)}${display.priceSuffix}`;

  // Truthful recurring-charge disclosure — both framings auto-renew.
  const disclosure = isPass
    ? `You'll be charged ${formatMoney(plan.price, plan.currency)} ${display.intervalAdverb}` +
      (plan.visit_limit != null
        ? ` for ${plan.visit_limit} ${plan.visit_limit === 1 ? "visit" : "visits"}`
        : "") +
      `, renewing until you cancel.`
    : `You'll be charged ${formatMoney(plan.price, plan.currency)} ${display.intervalAdverb}, renewing until you cancel.`;

  function handleSubscribe() {
    if (!chargeReadyPmId || !facilityId || !planId || paymentLocked) return;
    // Block a second subscribe while an SCA challenge is pending — re-firing
    // would create a SECOND Stripe subscription (the duplicate guard only
    // blocks active/past_due, not incomplete). The member completes the pending
    // challenge instead.
    if (pendingSca || pendingPurchase || uncertainPurchase) return;
    setScaError(false);
    subscribe.mutate(
      {
        facility_id: facilityId,
        plan_id: planId,
        stripe_payment_method_id: chargeReadyPmId,
      },
      {
        onSuccess: (res) => {
          if (
            res.user_id !== actorId ||
            res.facility_id !== facilityId ||
            res.plan_id !== planId
          ) {
            setScaError(true);
            setUncertainPurchase(true);
            return;
          }
          // First charge needs 3-D Secure → drive the challenge, then confirm
          // server-side. Otherwise (immediate active) the flow is unchanged.
          if (
            res.requires_action &&
            res.client_secret &&
            res.stripe_connect_account_id
          ) {
            setPendingSca({
              subscriptionId: res.subscription_id,
              clientSecret: res.client_secret,
              account: res.stripe_connect_account_id,
            });
          } else if (res.status === "active" && res.pending_state === null) {
            setSuccess(res);
          } else {
            setPendingPurchase({ ...res, client_secret: null });
          }
        },
        onError: (failure) => {
          // Validation rejects admission; the canonical CARD_DECLINED response
          // follows durable terminalization of a definitive provider rejection.
          // Other failures may already have persisted the original purchase/card.
          if (
            !isApiError(failure) ||
            !(
              failure.status === 422 ||
              (failure.status === 402 && failure.code === "CARD_DECLINED")
            )
          )
            setUncertainPurchase(true);
        },
      },
    );
  }

  // Called once stripe.confirmCardPayment resolves the 3-D Secure challenge.
  // Reconcile the subscription against Stripe: show success ONLY when the
  // charge actually cleared (active); a still-incomplete result is an honest
  // retry, never a fabricated success.
  async function handleScaAuthenticated() {
    if (!pendingSca)
      throw new Error("Payment authentication is no longer available");
    const confirmed = await confirmSubscription.mutateAsync(
      pendingSca.subscriptionId,
    );
    if (
      confirmed.subscription_id !== pendingSca.subscriptionId ||
      confirmed.user_id !== actorId ||
      confirmed.stripe_connect_account_id !== pendingSca.account
    )
      throw new Error("Membership payment confirmation changed scope");
    if (confirmed.status !== "active" || confirmed.pending_state !== null)
      throw new Error("Membership payment confirmation is still pending");
    setPendingSca(null);
    setScaError(false);
    setSuccess({ ...confirmed, client_secret: null });
  }

  // ---- Error mapping ---------------------------------------------------------
  const err = subscribe.error;
  const errStatus = isApiError(err) ? err.status : null;
  const declined =
    !uncertainPurchase && (errStatus === 402 || errStatus === 422);
  const facilityNotReady = !uncertainPurchase && errStatus === 400;
  const authError = !uncertainPurchase && errStatus === 403;
  const genericError =
    subscribe.isError &&
    !uncertainPurchase &&
    !declined &&
    !facilityNotReady &&
    !authError;

  const ctaLabel = subscribe.isPending
    ? isPass
      ? "Buying…"
      : "Subscribing…"
    : isPass
      ? "Buy a pass"
      : "Subscribe";

  return (
    <Page title={plan.name}>
      <Stack spacing={3}>
        {/* Plan summary — every value is the server's plan verbatim. */}
        <Stack component="section" aria-label="Plan details" spacing={1}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{
              justifyContent: "space-between",
              alignItems: { sm: "baseline" },
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {priceLabel}
            </Typography>
            <Typography color="text.secondary">
              {isPass && plan.visit_limit != null
                ? `${plan.visit_limit} ${plan.visit_limit === 1 ? "visit" : "visits"} per period`
                : "Unlimited access"}
            </Typography>
          </Stack>
          {plan.description ? (
            <Typography>{plan.description}</Typography>
          ) : null}
          {access ? (
            <Typography variant="body2" color="text.secondary">
              Access: {access}
            </Typography>
          ) : null}
        </Stack>

        {/* Truthful auto-renew disclosure — always visible before payment. */}
        <Alert severity="info" role="note">
          {disclosure}
        </Alert>

        {/* Payment — the reused facility-scoped Stripe spine. */}
        <Stack component="section" aria-label="Payment" spacing={2}>
          {configQ.isError ? (
            <Alert severity="error">
              This facility can&apos;t take card payments yet. Please try again
              later.
            </Alert>
          ) : paymentMethodsUnavailable ? (
            <PaymentMethodsLoadError
              retrying={methodsQ.isFetching}
              onRetry={() => void methodsQ.refetch()}
            />
          ) : configQ.isLoading || methodsQ.isLoading ? (
            <Stack
              direction="row"
              spacing={1.5}
              role="status"
              sx={{ alignItems: "center" }}
            >
              <CircularProgress size={20} />
              <Typography color="text.secondary">
                Loading payment options…
              </Typography>
            </Stack>
          ) : (
            <>
              <MaterialPaymentMethodPicker
                methods={methods}
                selectedId={selectedPmId}
                onSelect={selectPaymentMethod}
                disabled={paymentLocked}
                allowRemove
                facilityId={facilityId ?? ""}
                paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(
                  facilityId ?? "",
                )}
              />
              {configQ.data ? (
                <MaterialConnectedElementsProvider config={configQ.data}>
                  {!pendingSca && !pendingPurchase && !uncertainPurchase ? (
                    <MaterialAddCardForm
                      publishableKey={configQ.data.publishable_key}
                      createSetupIntent={createSetupIntentSecret}
                      onBusyChange={setCardSetupBusy}
                      onCardAdded={async (pmId) => {
                        const refreshed = await methodsQ.refetch();
                        const savedMethod = refreshed.data?.find(
                          (method) => method.payment_method_id === pmId,
                        );
                        if (
                          refreshed.isError ||
                          savedMethod?.is_usable !== true
                        ) {
                          throw new Error(
                            "The saved card is not available yet. Retry to refresh it.",
                          );
                        }
                        selectPaymentMethod(pmId);
                      }}
                    />
                  ) : null}
                </MaterialConnectedElementsProvider>
              ) : null}
            </>
          )}
        </Stack>

        {pendingSca ? (
          <Stack spacing={1.5}>
            <PurchasePaymentAuthentication
              clientSecret={pendingSca.clientSecret}
              account={pendingSca.account}
              onAuthenticated={handleScaAuthenticated}
            />
            <Button
              component={RouterLink}
              to={`/me/memberships/${pendingSca.subscriptionId}`}
              size="small"
            >
              Review membership
            </Button>
          </Stack>
        ) : null}
        {pendingPurchase ? (
          <Alert severity="info">
            {deriveSubscriptionView(pendingPurchase).label}. Review this
            recorded purchase from My memberships.
            <Button
              component={RouterLink}
              to={`/me/memberships/${pendingPurchase.subscription_id}`}
              size="small"
            >
              Review membership
            </Button>
          </Alert>
        ) : null}
        {scaError ? (
          <Alert severity="error">
            We couldn&apos;t confirm this membership. Review My memberships
            before trying again.
          </Alert>
        ) : null}
        {uncertainPurchase ? (
          <Alert severity="warning">
            {errStatus === 409
              ? "An existing purchase needs review."
              : "Something went wrong. This purchase outcome is not confirmed."}{" "}
            Your original card choice is locked; review My memberships before
            another purchase.
            <Button component={RouterLink} to="/me/memberships" size="small">
              My memberships
            </Button>
          </Alert>
        ) : null}
        {/* Inline errors — honest, never a fake success. */}
        {declined ? (
          <Alert severity="error">
            Your card was declined. Try a different card and confirm again.
          </Alert>
        ) : null}
        {facilityNotReady ? (
          <Alert severity="error">
            This facility isn&apos;t ready to sell memberships yet. Please try
            again later.
          </Alert>
        ) : null}
        {authError ? (
          <Alert severity="error">
            You&apos;re not authorized to buy this membership.
          </Alert>
        ) : null}
        {genericError ? (
          <Alert severity="error">
            Something went wrong. Please try again.
          </Alert>
        ) : null}

        {/* Primary CTA — disabled until a card is chosen (and while the config
            can't take payments); busy during the server-side charge. Hidden
            while an SCA challenge is pending (the member finishes that via the
            "Verify with your bank" control instead of re-subscribing). Only
            rendered when the facility CAN take card payments. */}
        {!configQ.isError &&
        !paymentMethodsUnavailable &&
        !pendingSca &&
        !pendingPurchase ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubscribe}
              disabled={!chargeReadyPmId || paymentLocked || configQ.isLoading}
              aria-busy={subscribe.isPending}
            >
              {ctaLabel}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  withFacilityDiscoveryReturn(
                    `/facility/${facilityId}/memberships`,
                    facilitySearchReturn,
                  ),
                )
              }
            >
              Back to plans
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// SCA (3-D Secure) challenge for the subscription's first charge. Must render
// inside a <ConnectedElementsProvider> so useStripe() is scoped to the recorded
// purchase's connected account (the PaymentIntent client_secret only confirms
// under that account).
//
// The player drives the challenge with an explicit click (a user gesture — the
// most reliable trigger for the 3-D Secure popup, free of StrictMode
// effect-double-fire). On a confirmed PaymentIntent it calls onAuthenticated()
// so the parent reconciles the subscription server-side (success shown ONLY
// when it goes active); a failed/cancelled challenge OR a server reconcile that
// is still not-cleared (retryError) surfaces an honest retry. The BACKEND stays
// the gate — this only completes the Stripe-side authentication.
// ---------------------------------------------------------------------------

function PurchasePaymentAuthentication({
  clientSecret,
  onAuthenticated,
  account,
}: {
  clientSecret: string;
  onAuthenticated: () => Promise<void>;
  account: string;
}) {
  const config = useWalletFrontendConfig();
  if (config.isError)
    return (
      <Alert severity="error">
        Payment authentication is unavailable. Review My memberships.
      </Alert>
    );
  if (!config.data)
    return (
      <Typography role="status">Loading payment authentication…</Typography>
    );
  return (
    <MaterialConnectedElementsProvider
      config={{
        ...config.data,
        connected_account_id: account,
        stripe_account_id: account,
      }}
    >
      <MaterialPaymentIntentAuthentication
        clientSecret={clientSecret}
        onAuthenticated={onAuthenticated}
        authenticationFailureMessage="We couldn't confirm this payment. Try again or review your recorded membership."
      />
    </MaterialConnectedElementsProvider>
  );
}
